import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { motion } from "framer-motion";
import { FileUpload } from "@/components/FileUpload";
import { DataPreview } from "@/components/DataPreview";
import { VariablePills } from "@/components/VariablePills";
import { EmailEditor } from "@/components/EmailEditor";
import { EmailPreview } from "@/components/EmailPreview";
import { SmtpConfig } from "@/components/SmtpConfig";
import { SendPanel } from "@/components/SendPanel";
import { Attachments } from "@/components/Attachments";
import { SignatureEditor } from "@/components/SignatureEditor";
import { Mail, Sparkles, LogOut, Shield, User, KeyRound, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function DashboardPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [fileData, setFileData] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [activeField, setActiveField] = useState("body");
  const [smtpConfig, setSmtpConfig] = useState({
    host: "",
    port: 587,
    email: "",
    password: "",
    name: "",
  });
  const [attachments, setAttachments] = useState([]);
  const editorRef = useRef(null);

  const handleDataLoaded = useCallback((data) => {
    setFileData(data);
  }, []);

  const handleInsertVariable = useCallback(
    (variable) => {
      if (activeField === "subject") {
        setSubject((prev) => prev + variable);
      } else {
        const editor = editorRef.current;
        if (editor) {
          editor.focus();
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            if (editor.contains(range.commonAncestorContainer)) {
              document.execCommand("insertHTML", false, variable);
              setBody(editor.innerHTML);
              return;
            }
          }
          editor.focus();
          document.execCommand("insertHTML", false, variable);
          setBody(editor.innerHTML);
        } else {
          setBody((prev) => prev + variable);
        }
      }
    },
    [activeField]
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const emailColumn = fileData
    ? fileData.columns.find((c) => c.toLowerCase() === "email") || "email"
    : "email";

  return (
    <div className="min-h-screen bg-white" data-testid="app-container">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="border-b border-zinc-100 bg-white/80 backdrop-blur-md sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-zinc-900 leading-tight tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                netlinkMailing
              </h1>
              <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">
                Emailing personnalisé
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {fileData && (
              <EmailPreview
                subject={subject}
                body={body}
                firstRow={fileData.rows[0]}
                senderEmail={smtpConfig.email}
              />
            )}
            <SmtpConfig smtpConfig={smtpConfig} onConfigChange={setSmtpConfig} />

            <Separator orientation="vertical" className="h-6" />

            <Badge variant="outline" className="text-xs gap-1.5 py-1.5 px-3">
              <User className="w-3 h-3" />
              {user?.full_name || user?.username}
            </Badge>

            <ChangeMyPasswordDialog />

            {isAdmin && (
              <Button variant="outline" size="sm" className="gap-2 rounded-full" onClick={() => navigate("/admin")} data-testid="admin-link">
                <Shield className="w-3.5 h-3.5" />
                Admin
              </Button>
            )}

            <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-400 hover:text-zinc-700" onClick={handleLogout} title="Déconnexion" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero section when no file */}
        {!fileData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center mb-12"
            data-testid="hero-section"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Emailing en masse personnalisé
            </div>
            <h2
              className="text-4xl sm:text-5xl font-black text-zinc-900 tracking-tight leading-tight mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Envoyez des emails<br />
              <span className="text-primary">personnalisés</span> à grande échelle
            </h2>
            <p className="text-base text-zinc-500 max-w-md mx-auto leading-relaxed">
              Importez votre fichier, créez votre template avec des variables dynamiques, et envoyez.
            </p>
          </motion.div>
        )}

        {/* Upload Section */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`${fileData ? "" : "max-w-2xl mx-auto"}`}
          data-testid="upload-section"
        >
          <FileUpload onDataLoaded={handleDataLoaded} />
        </motion.section>

        {/* Content when file is loaded */}
        {fileData && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
            {/* Left Column - Data + Variables */}
            <div className="lg:col-span-5 space-y-8">
              <DataPreview
                columns={fileData.columns}
                rows={fileData.rows}
                totalRows={fileData.totalRows}
              />

              <Separator />

              <VariablePills
                columns={fileData.columns}
                onInsert={handleInsertVariable}
              />
            </div>

            {/* Right Column - Editor + Send */}
            <div className="lg:col-span-7 space-y-8">
              <EmailEditor
                subject={subject}
                body={body}
                onSubjectChange={setSubject}
                onBodyChange={setBody}
                activeField={activeField}
                onFieldFocus={setActiveField}
                editorRef={editorRef}
              />

              <Separator />

              <Attachments
                attachments={attachments}
                onAttachmentsChange={setAttachments}
              />

              <Separator />

              <SignatureEditor />

              <Separator />

              <SendPanel
                smtpConfig={smtpConfig}
                subject={subject}
                body={body}
                rows={fileData.rows}
                emailColumn={emailColumn}
                attachments={attachments}
              />
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-100 py-6 mt-12" data-testid="app-footer">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <p className="text-xs text-zinc-400">
            netlinkMailing — Outil d'emailing personnalisé - Netlink Solutions
          </p>
          <p className="text-xs text-zinc-400">
            Connecté en tant que {user?.full_name || user?.username}
          </p>
        </div>
      </footer>
    </div>
  );
}


function ChangeMyPasswordDialog() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      toast.error("Remplissez tous les champs");
      return;
    }
    if (newPassword.length < 4) {
      toast.error("Le nouveau mot de passe doit contenir au moins 4 caractères");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Mot de passe modifié avec succès");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-400 hover:text-zinc-700" title="Changer mon mot de passe" data-testid="change-password-button">
          <KeyRound className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary" />
            Changer mon mot de passe
          </DialogTitle>
          <DialogDescription>
            Entrez votre mot de passe actuel puis le nouveau
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm">Mot de passe actuel</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Votre mot de passe actuel"
              autoFocus
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Nouveau mot de passe</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 4 caractères"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Confirmer le nouveau mot de passe</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Retapez le nouveau mot de passe"
              data-testid="confirm-password-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" className="flex-1 rounded-full gap-2" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Modifier
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
